import { useDebouncedCallback } from "@mantine/hooks";
import { useFormContext, useWatch } from "react-hook-form";
import { z } from "zod";

import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { NativeSelectInputController } from "#shared/view/designSystem/inputs/NativeSelectInputController";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { CustomInputResponseSchema } from "shared";

import { useUpsertCustomInput } from "../useUpsertCustomInput";
import { INPUT_MIN_WIDTH } from "./constants";

type Props = {
  customInput: z.infer<typeof CustomInputResponseSchema>;
  index: number;
  quoteRequestLineId: string | undefined;
  maw?: number;
  disabled?: boolean;
};

export const CustomInputController = ({
  customInput,
  index,
  quoteRequestLineId,
  maw,
  disabled,
}: Props) => {
  const { control, getValues } = useFormContext<QuoteRequestLineFormData>();

  const { upsertCustomInput } = useUpsertCustomInput({ quoteRequestLineId });

  const value = useWatch({ control, name: `customInputs.${index}.value` as const });

  const handleApiUpdate = (newValue: string | number | null) => {
    if (!quoteRequestLineId) {
      return;
    }

    const customInputId = getValues(`customInputs.${index}.id`);

    upsertCustomInput({
      id: quoteRequestLineId,
      payload: {
        ...customInput,
        id: customInputId,
        value: newValue?.toString() || "",
      },
    });
  };

  const debouncedHandleApiUpdate = useDebouncedCallback(handleApiUpdate, 500);

  switch (customInput.type) {
    case "number": {
      const intValue = typeof value === "number" ? value : value ? parseInt(value) : null;
      return (
        <NumberInputController
          control={control}
          name={`customInputs.${index}.value`}
          label={customInput.label}
          value={intValue ?? customInput.defaultValue ?? ""}
          onValueChange={(values) => {
            debouncedHandleApiUpdate(values.value);
          }}
          w={maw}
          miw={INPUT_MIN_WIDTH}
          disabled={disabled}
        />
      );
    }
    case "select": {
      return (
        <NativeSelectInputController
          control={control}
          name={`customInputs.${index}.value`}
          label={customInput.label}
          data={customInput.options}
          onChange={debouncedHandleApiUpdate}
          w={maw}
          miw={INPUT_MIN_WIDTH}
          disabled={disabled}
          defaultValue={customInput.defaultValue}
        />
      );
    }
  }
};
